/**
 * GodConsole
 *
 * Platform-level workspaces console — accessible only to Gods (platform_role=god).
 * Route: /admin
 *
 * Tabs:
 *   - Workspaces: list all workspaces; enter (switch into) any; create a new one.
 *
 * Creator (waiting-list) requests are decided in Settings -> Waiting List,
 * not here.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Tabs,
    Table,
    Button,
    Tag,
    Input,
    Modal,
    Tooltip,
    message,
    Empty,
} from 'antd';
import {
    LoginOutlined,
    PlusOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/features/auth/AuthContext.jsx';
import { get_base_url } from '@/shared/http/client';

function getBase() {
    return get_base_url(window.location.origin);
}

// ─────────────────────────────────────────────
// Workspaces tab
// ─────────────────────────────────────────────

function WorkspacesTab() {
    const { workspace: activeWorkspace, switchWorkspace } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [switching, setSwitching] = useState(null); // id of workspace being entered

    const fetchWorkspaces = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await fetch(`${getBase()}/workspaces`, { credentials: 'include' });
            if (resp.ok) {
                const data = await resp.json();
                setWorkspaces(data.workspaces || []);
            }
        } catch (_) {
            message.error('Failed to load workspaces.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);

    const handleEnter = async (wsId) => {
        setSwitching(wsId);
        try {
            await switchWorkspace(wsId);
            message.success('Switched workspace.');
            window.location.href = '/';
        } catch (err) {
            message.error(err?.message || 'Failed to switch workspace.');
            setSwitching(null);
        }
    };

    const handleCreate = async () => {
        const trimmed = newName.trim();
        if (!trimmed) { message.warning('Workspace name is required.'); return; }
        setCreating(true);
        try {
            const resp = await fetch(`${getBase()}/workspaces`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: trimmed }),
            });
            if (resp.ok) {
                message.success(`Workspace "${trimmed}" created.`);
                setCreateOpen(false);
                setNewName('');
                fetchWorkspaces();
            } else {
                const err = await resp.json().catch(() => ({}));
                message.error(err?.detail || 'Failed to create workspace.');
            }
        } catch (_) {
            message.error('Failed to create workspace.');
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name, row) => (
                <span>
                    {name}
                    {row.id === activeWorkspace?.id && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>Active</Tag>
                    )}
                    {row.is_default && (
                        <Tag color="default" style={{ marginLeft: 4 }}>Migrated</Tag>
                    )}
                </span>
            ),
        },
        {
            title: 'Owner',
            dataIndex: 'owner_email',
            key: 'owner_email',
            render: (v) => v || '—',
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 140,
            render: (v) => v ? new Date(v).toLocaleDateString() : '—',
        },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, row) => (
                <Tooltip title="Enter workspace">
                    <Button
                        size="small"
                        icon={<LoginOutlined />}
                        loading={switching === row.id}
                        disabled={switching !== null}
                        onClick={() => handleEnter(row.id)}
                    >
                        Enter
                    </Button>
                </Tooltip>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Button icon={<ReloadOutlined />} onClick={fetchWorkspaces} loading={loading}>
                    Refresh
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                    New Workspace
                </Button>
            </div>
            <Table
                dataSource={workspaces}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                locale={{ emptyText: <Empty description="No workspaces yet" /> }}
            />

            <Modal
                title="Create workspace"
                open={createOpen}
                onCancel={() => { setCreateOpen(false); setNewName(''); }}
                onOk={handleCreate}
                confirmLoading={creating}
                okText="Create"
            >
                <Input
                    placeholder="Workspace name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onPressEnter={handleCreate}
                    maxLength={100}
                    autoFocus
                />
            </Modal>
        </div>
    );
}

// ─────────────────────────────────────────────
// Main GodConsole
// ─────────────────────────────────────────────

const TAB_ITEMS = [
    {
        key: 'workspaces',
        label: 'Workspaces',
        children: <WorkspacesTab />,
    },
];

export default function GodConsole() {
    return (
        <div style={{ maxWidth: 900, margin: '32px auto', padding: '0 24px' }}>
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Admin Console</h1>
                <p style={{ color: '#6b7280', marginTop: 4 }}>Platform administration — visible to Gods only.</p>
            </div>
            <Tabs defaultActiveKey="workspaces" items={TAB_ITEMS} />
        </div>
    );
}
